import { Skeleton, Typography } from '@/components/atoms';
import type { FormPickerOption } from '@/components/molecules/FormPicker';
import { OptionPickerBottomSheet } from '@/components/molecules/optionPicker/OptionPickerBottomSheet';
import {
    OPTION_PICKER_DROPDOWN_MAX_HEIGHT,
    type OptionPickerModalProps,
} from '@/components/molecules/optionPicker/OptionPickerModal.types';

const DROPDOWN_LIST_CONTENT_PADDING_VERTICAL = 4;
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
    OPTION_PICKER_DROPDOWN_ROW_HEIGHT,
    useScrollOptionPickerToSelected,
} from '@/components/molecules/optionPicker/useScrollOptionPickerToSelected';
import { BREAKPOINTS, getBreakpointWidth } from '@/core/utils/responsive';
import React, { useMemo, useRef } from 'react';
import {
    FlatList,
    Modal,
    Pressable,
    StyleSheet,
    TouchableOpacity,
    View,
    type ViewStyle,
} from 'react-native';

export type {
    OptionPickerAnchorRect,
    OptionPickerModalProps,
} from '@/components/molecules/optionPicker/OptionPickerModal.types';

const DROPDOWN_GAP = 4;

function OptionPickerMatDropdown({
    visible,
    options,
    selectedKey,
    onSelect,
    onClose,
    loading = false,
    anchor,
}: Readonly<OptionPickerModalProps>) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const { t } = useTranslation();
    const listRef = useRef<FlatList<FormPickerOption>>(null);
    const { getItemLayout, onScrollToIndexFailed } = useScrollOptionPickerToSelected({
        listRef,
        visible,
        loading,
        options,
        selectedKey,
        rowHeight: OPTION_PICKER_DROPDOWN_ROW_HEIGHT,
        contentPaddingVertical: DROPDOWN_LIST_CONTENT_PADDING_VERTICAL,
        listViewportHeight: OPTION_PICKER_DROPDOWN_MAX_HEIGHT,
    });

    const panelStyle = useMemo((): ViewStyle => {
        if (!anchor) {
            return { opacity: 0 };
        }

        const opensUpward = anchor.opensUpward === true;
        let top = opensUpward
            ? anchor.y - OPTION_PICKER_DROPDOWN_MAX_HEIGHT - DROPDOWN_GAP
            : anchor.y + anchor.height - 1;

        if (typeof window !== 'undefined') {
            top = Math.max(8, Math.min(top, window.innerHeight - OPTION_PICKER_DROPDOWN_MAX_HEIGHT - 8));
        }

        const panelWidth = anchor.width;

        const shared: ViewStyle = {
            position: 'absolute',
            top: Math.round(top),
            left: Math.round(anchor.x),
            width: panelWidth,
            minWidth: panelWidth,
            maxWidth: panelWidth,
            maxHeight: OPTION_PICKER_DROPDOWN_MAX_HEIGHT,
            backgroundColor: colors.backgroundLevel1,
            borderWidth: 1,
            borderColor: colors.outlineActive,
            overflow: 'hidden',
            zIndex: 2,
            ...( {
                boxSizing: 'border-box',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
            } as ViewStyle),
        };

        if (opensUpward) {
            return {
                ...shared,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
            };
        }

        return {
            ...shared,
            borderTopWidth: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
        };
    }, [anchor, colors.backgroundLevel1, colors.outlineActive]);

    if (!anchor || !visible) {
        return null;
    }

    return (
        <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
            <Pressable style={styles.backdrop} onPress={onClose} accessibilityRole="button" />
            <View style={panelStyle} pointerEvents="box-none">
                <FlatList
                    ref={listRef}
                    data={loading ? Array.from({ length: 6 }) : options}
                    keyExtractor={(item, idx) =>
                        (item as FormPickerOption)?.key ||
                        (item as FormPickerOption)?.id ||
                        `skeleton-${idx}`
                    }
                    style={styles.list}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator
                    keyboardShouldPersistTaps="handled"
                    getItemLayout={loading ? undefined : getItemLayout}
                    onScrollToIndexFailed={onScrollToIndexFailed}
                    ListEmptyComponent={
                        <View style={styles.modalEmpty}>
                            <Typography variant="body" color="tertiary" style={{ textAlign: 'center' }}>
                                {t('support.emptyState')}
                            </Typography>
                        </View>
                    }
                    renderItem={({ item }) => {
                        if (loading) {
                            return <Skeleton height={44} borderRadius={0} />;
                        }
                        const option = item as FormPickerOption;
                        const itemKey = option.key || option.id;
                        const isSelected = itemKey === selectedKey;
                        return (
                            <TouchableOpacity
                                activeOpacity={0.7}
                                style={[
                                    styles.optionRow,
                                    isSelected && {
                                        backgroundColor: colors.backgroundLevel3,
                                    },
                                ]}
                                onPress={() => onSelect(option)}
                            >
                                <Typography
                                    variant="body"
                                    color="primary"
                                    style={{
                                        flex: 1,
                                        textAlign: isRTL ? 'right' : 'left',
                                    }}
                                    numberOfLines={2}
                                >
                                    {option.label}
                                </Typography>
                            </TouchableOpacity>
                        );
                    }}
                />
            </View>
        </Modal>
    );
}

/** Web: mat-select menu on desktop; bottom sheet on compact widths. */
export function OptionPickerModal(props: Readonly<OptionPickerModalProps>) {
    const isCompactWeb = getBreakpointWidth() < BREAKPOINTS.PHONE;

    if (isCompactWeb) {
        return <OptionPickerBottomSheet {...props} />;
    }

    return <OptionPickerMatDropdown {...props} />;
}

const styles = StyleSheet.create({
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'transparent',
    },
    list: {
        flexGrow: 0,
        width: '100%',
    },
    listContent: {
        paddingVertical: 4,
    },
    modalEmpty: {
        paddingVertical: 16,
        paddingHorizontal: 12,
        alignItems: 'center',
    },
    optionRow: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        minHeight: 44,
        justifyContent: 'center',
        width: '100%',
    },
});
